import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PromoCountdown } from "../components/Bits";
import { Check, LogOut, Bell, BellOff, Volume2, MessageSquare } from "lucide-react";
import { pushSupported, getPushSubscription, enablePush, disablePush } from "../lib/push";

function NotificationsCard() {
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("hr_sound") !== "off");
  useEffect(() => {
    getPushSubscription().then((s) => setPushOn(Boolean(s))).catch(() => {});
  }, []);
  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("Push notifications turned off");
      } else {
        await enablePush();
        setPushOn(true);
        toast.success("Push notifications ON — you'll get HR alerts, line steam and board drops");
      }
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    }
    setBusy(false);
  };
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("hr_sound", next ? "on" : "off");
    toast.success(next ? "HR celebration sound ON" : "HR celebration sound muted");
  };
  const rowCls = "flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0";
  const btnCls = (on) => `flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold px-4 py-2 rounded border transition-colors duration-200 ${on ? "border-[#22c55e]/60 text-[#22c55e] bg-[#22c55e]/10" : "border-white/20 text-neutral-400 hover:border-[#a855f7] hover:text-[#a855f7]"}`;
  return (
    <div className="card-surface rounded-xl p-6 fade-up-2" data-testid="settings-notifications">
      <h3 className="font-display text-lg font-bold uppercase text-[#a855f7]">Alerts & Notifications</h3>
      <div className="mt-2">
        <div className={rowCls}>
          <div>
            <div className="text-sm font-semibold">Push notifications</div>
            <p className="text-xs text-neutral-500">Home run alerts, line steam and board drops — even when the site is closed.{!pushSupported() && " Not supported on this browser."}</p>
          </div>
          <button onClick={togglePush} disabled={busy || !pushSupported()} data-testid="push-toggle-btn" className={btnCls(pushOn)}>
            {pushOn ? <Bell size={13} /> : <BellOff size={13} />} {pushOn ? "On" : "Off"}
          </button>
        </div>
        <div className={rowCls}>
          <div>
            <div className="text-sm font-semibold">HR celebration sound</div>
            <p className="text-xs text-neutral-500">Bat crack + crowd roar when a homer lands while you're on the site.</p>
          </div>
          <button onClick={toggleSound} data-testid="hr-sound-settings-btn" className={btnCls(soundOn)}>
            <Volume2 size={13} /> {soundOn ? "On" : "Off"}
          </button>
        </div>
        <div className={rowCls}>
          <div>
            <div className="text-sm font-semibold">Email — morning board</div>
            <p className="text-xs text-neutral-500">Pro & VIP members get the board by email the moment it posts. Automatic with your membership.</p>
          </div>
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#22c55e]">Included</span>
        </div>
        <div className={rowCls}>
          <div>
            <div className="text-sm font-semibold flex items-center gap-2"><MessageSquare size={13} className="text-neutral-500" /> SMS alerts</div>
            <p className="text-xs text-neutral-500">Text alerts for VIP locks and steam moves.</p>
          </div>
          <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 border border-white/15 px-2.5 py-1 rounded">Coming soon</span>
        </div>
      </div>
    </div>
  );
}


const TIERS = [
  { key: "free", name: "Free", price: "$0", features: ["1 daily pick", "Basic research", "Hit Tracker", "EV Calculator"] },
  { key: "pro", name: "Pooh Sheet Pro", price: "$29", features: ["Full daily board", "Research Center", "Odds Tracker", "Parlay Builder", "Early picks"] },
  { key: "vip", name: "VIP", price: "$79", features: ["Everything in Pro", "Live updates", "Premium alerts", "VIP community channel"] },
];

function dayPassActive(user) {
  return user?.day_pass_until && new Date(user.day_pass_until) > new Date();
}

export default function Settings() {
  const { user, setUser, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [payState, setPayState] = useState(null); // null | checking | paid | failed
  const [upgrading, setUpgrading] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const polled = useRef(false);
  const { data: promo } = useQuery({ queryKey: ["promoMe"], queryFn: () => api.get("/promo/me").then((r) => r.data) });
  const { data: subPromo } = useQuery({ queryKey: ["subPromo"], queryFn: () => api.get("/sub-promo").then((r) => r.data) });

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || polled.current) return;
    polled.current = true;
    setPayState("checking");
    let attempts = 0;
    const poll = async () => {
      if (attempts++ >= 6) {
        setPayState("failed");
        toast.error("Payment status check timed out. Contact support if you were charged.");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayState("paid");
          await refreshMe();
          toast.success(data.package_id === "daypass" ? "Day Pass activated! You have full access for the next 24 hours." : data.package_id === "vip" ? "Welcome to VIP! Your private Discord invite is below." : `Welcome to ${data.package_id.toUpperCase()}! Your membership is active.`);
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          setPayState("failed");
          toast.error("Payment session expired. Please try again.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setPayState("failed");
        toast.error(apiError(e));
      }
    };
    poll();
  }, [searchParams, refreshMe, setSearchParams]);

  const upgrade = async (packageId) => {
    setUpgrading(packageId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: packageId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
      setUpgrading(null);
    }
  };

  const saveName = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", { name });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Set<span className="gold-text">tings</span></h1>
      </div>

      <div className="card-surface rounded-xl p-6 fade-up-1">
        <h3 className="font-display text-lg font-bold uppercase text-[#a855f7]">Profile</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input"
              className="mt-1 w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2.5 text-sm outline-none transition-colors duration-200" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">Email</label>
            <input value={user?.email || ""} disabled className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2.5 text-sm text-neutral-500" />
          </div>
        </div>
        <button onClick={saveName} disabled={busy || !name.trim()} data-testid="settings-save-btn"
          className="mt-4 bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-40 text-black font-bold uppercase tracking-wider text-sm px-6 py-2.5 rounded transition-colors duration-200">
          Save
        </button>
      </div>

      <div className="fade-up-2">
        <h3 className="font-display text-lg font-bold uppercase text-white mb-3">Membership</h3>
        {payState === "checking" && (
          <div className="mb-4 border border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7] rounded-lg px-4 py-3 text-sm font-semibold animate-pulse" data-testid="payment-checking">
            Verifying your payment with Stripe...
          </div>
        )}
        {payState === "paid" && (
          <div className="mb-4 border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] rounded-lg px-4 py-3 text-sm font-semibold" data-testid="payment-success">
            Payment confirmed — your membership is active. Enjoy the full sheet.
          </div>
        )}
        {dayPassActive(user) && (
          <div className="mb-4 border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] rounded-lg px-4 py-3 text-sm font-semibold" data-testid="daypass-active-banner">
            Day Pass active. Full access until {new Date(user.day_pass_until).toLocaleString()}.
          </div>
        )}
        {user?.tier === "free" && user?.role !== "admin" && !dayPassActive(user) && (() => {
          const promoDeal = promo?.active && promo?.eligible;
          return (
          <div className={`mb-4 card-gold border rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${promoDeal ? "border-[#22c55e]/50 gold-glow" : "border-[#a855f7]/30"}`} data-testid="settings-daypass-card">
            <div className="flex-1">
              {promoDeal ? (
                <div className="inline-flex items-center gap-1.5 bg-[#22c55e]/15 border border-[#22c55e]/50 text-[#22c55e] text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full" data-testid="promo-badge">
                  $1 Launch Promo · First-time members · {promo?.ends_at ? <PromoCountdown endsAt={promo.ends_at} /> : "Limited time"}
                </div>
              ) : (
                <div className="text-[11px] uppercase tracking-widest text-[#a855f7] font-bold">Big slate tonight?</div>
              )}
              <h4 className="font-display text-xl font-bold uppercase mt-1.5">24 Hour Day Pass — $9.99</h4>
              <p className="text-xs text-neutral-400 mt-1">One time payment. Full Pro access for 24 hours: complete board, Research Center, Live HR Tracker, Weak Spots, BvP and more. No subscription.</p>
            </div>
            <button onClick={() => upgrade("daypass")} disabled={upgrading !== null} data-testid="upgrade-daypass-btn"
              className={`shrink-0 disabled:opacity-40 text-black font-bold uppercase tracking-wider text-xs px-6 py-3 rounded transition-colors duration-200 ${promoDeal ? "bg-[#22c55e] hover:bg-[#1ea34f]" : "bg-[#a855f7] hover:bg-[#9333ea]"}`}>
              {upgrading === "daypass" ? "Redirecting to Stripe..." : promoDeal ? (
                <span>Day Pass — <s className="opacity-60">$9.99</s> $1.00</span>
              ) : "Day Pass — $9.99"}
            </button>
          </div>
          );
        })()}
        <div className="grid md:grid-cols-3 gap-4">
          {TIERS.map((t) => {
            const current = user?.tier === t.key;
            const upgradable = t.key !== "free" && !current && user?.role !== "admin";
            const base = { pro: 29, vip: 79 }[t.key];
            const discounted = subPromo?.active && base ? (base * (1 - subPromo.pct / 100)).toFixed(2) : null;
            return (
              <div key={t.key} data-testid={`settings-tier-${t.key}`}
                className={`rounded-xl p-5 border flex flex-col ${current ? "border-[#a855f7] bg-[#a855f7]/5 gold-glow" : "border-white/10 card-surface"}`}>
                <div className="flex items-center justify-between">
                  <h4 className="font-display text-xl font-bold uppercase">{t.name}</h4>
                  {current && <span className="text-[10px] uppercase font-bold bg-[#a855f7] text-black px-2 py-0.5 rounded">Current</span>}
                  {!current && discounted && <span className="text-[10px] uppercase font-bold bg-[#22c55e] text-black px-2 py-0.5 rounded glow-pulse" data-testid={`subpromo-badge-${t.key}`}>{subPromo.pct}% OFF</span>}
                </div>
                {discounted ? (
                  <div className="font-data text-2xl font-bold mt-1">
                    <span className="text-neutral-500 line-through text-base mr-2">{t.price}</span>
                    <span className="text-[#22c55e]">${discounted}</span><span className="text-neutral-500 text-sm">/mo</span>
                  </div>
                ) : (
                  <div className="font-data text-2xl font-bold mt-1">{t.price}<span className="text-neutral-500 text-sm">/mo</span></div>
                )}
                <ul className="mt-3 space-y-1.5 flex-1">
                  {t.features.map((f, i) => <li key={i} className="flex gap-2 text-xs text-neutral-400"><Check size={13} className="text-[#22c55e] shrink-0 mt-0.5" />{f}</li>)}
                </ul>
                {upgradable && (
                  <button onClick={() => upgrade(t.key)} disabled={upgrading !== null} data-testid={`upgrade-${t.key}-btn`}
                    className={`mt-4 w-full ${discounted ? "btn-shine" : "bg-[#a855f7]"} hover:bg-[#9333ea] disabled:opacity-40 text-black font-bold uppercase tracking-wider text-xs py-2.5 rounded transition-colors duration-200`}>
                    {upgrading === t.key ? "Redirecting to Stripe..." : `Upgrade — ${discounted ? `$${discounted}` : t.price}/mo`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-neutral-500 text-xs mt-4">
          Payments are processed securely by Stripe. Your tier activates immediately after checkout.
        </p>
      </div>

      <NotificationsCard />

      <div className="card-surface rounded-xl p-6 fade-up-2">
        <h3 className="font-display text-lg font-bold uppercase text-[#a855f7]">Account</h3>
        <p className="text-neutral-400 text-sm mt-2">Signed in as <span className="text-white font-semibold">{user?.email}</span>. Log out to end your session on this device.</p>
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="settings-logout-btn"
          className="mt-4 flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-red-400 border border-red-500/40 hover:bg-red-500/10 hover:border-red-400 rounded px-6 py-2.5 transition-colors duration-200"
        >
          <LogOut size={14} /> Log Out
        </button>
      </div>
    </div>
  );
}
